import { useState } from "react";
import { Mail, Phone, MapPin, MessageCircle, Send, Sparkles, Moon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { motion } from "framer-motion";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: ""
  });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.message) {
      toast.error("Please fill in all required fields");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/contact`, formData);
      toast.success("Message sent successfully! We'll get back to you soon. ✨");
      setFormData({ name: "", email: "", phone: "", message: "" });
    } catch (error) {
      console.error("Error sending message:", error);
      toast.error("Failed to send message. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-mystic-void pt-24" data-testid="contact-page">
      {/* Header */}
      <section className="py-16 px-6 md:px-12 lg:px-24 bg-gradient-to-b from-mystic-purple/30 to-mystic-void">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-2xl mx-auto"
          >
            <div className="flex items-center justify-center gap-2 mb-3">
              <Sparkles className="w-4 h-4 text-mystic-gold" />
              <p className="text-mystic-gold font-medium tracking-widest uppercase text-sm">
                Seek Guidance
              </p>
              <Sparkles className="w-4 h-4 text-mystic-gold" />
            </div>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light text-mystic-silver mb-6 italic text-glow">
              Connect with <br />
              <span className="text-mystic-orchid">Our Realm</span>
            </h1>
            <p className="text-mystic-mist text-lg">
              Have questions about our crystals or need spiritual guidance? 
              Reach out and we'll illuminate your path.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-16 px-6 md:px-12 lg:px-24">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="glass rounded-2xl p-8 md:p-10 border border-white/10">
                <h2 className="font-serif text-2xl text-mystic-silver mb-6 italic flex items-center gap-2">
                  <Moon className="w-6 h-6 text-mystic-gold" />
                  Send us a Message
                </h2>
                <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                  <div className="space-y-2">
                    <Label htmlFor="name" className="text-mystic-silver">
                      Name <span className="text-mystic-orchid">*</span>
                    </Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Your name"
                      className="bg-black/40 border-white/10 focus:border-mystic-amethyst text-mystic-silver placeholder:text-mystic-mist/30 rounded-lg"
                      data-testid="contact-name-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-mystic-silver">
                      Email <span className="text-mystic-orchid">*</span>
                    </Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="your@email.com"
                      className="bg-black/40 border-white/10 focus:border-mystic-amethyst text-mystic-silver placeholder:text-mystic-mist/30 rounded-lg"
                      data-testid="contact-email-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="phone" className="text-mystic-silver">
                      Phone (Optional)
                    </Label>
                    <Input
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="+91 98765 43210"
                      className="bg-black/40 border-white/10 focus:border-mystic-amethyst text-mystic-silver placeholder:text-mystic-mist/30 rounded-lg"
                      data-testid="contact-phone-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message" className="text-mystic-silver">
                      Message <span className="text-mystic-orchid">*</span>
                    </Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="How can we guide you?"
                      rows={5}
                      className="bg-black/40 border-white/10 focus:border-mystic-amethyst text-mystic-silver placeholder:text-mystic-mist/30 rounded-lg resize-none"
                      data-testid="contact-message-input"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-full py-6 text-base font-serif italic btn-lift neon-glow disabled:opacity-50"
                    data-testid="contact-submit-btn"
                  >
                    {loading ? (
                      "Sending..."
                    ) : (
                      <>
                        Send Message
                        <Send className="w-4 h-4 ml-2" />
                      </>
                    )}
                  </Button>
                </form>
              </div>
            </motion.div>

            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="space-y-8"
            >
              <div>
                <h2 className="font-serif text-2xl text-mystic-silver mb-6 italic">
                  Other Ways to Reach Us
                </h2>
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-full bg-mystic-amethyst/20 border border-mystic-amethyst/30 flex items-center justify-center flex-shrink-0">
                      <Mail className="w-5 h-5 text-mystic-amethyst" />
                    </div>
                    <div>
                      <p className="font-medium text-mystic-silver">Email Us</p>
                      <a 
                        href="mailto:hello@krystalz.in" 
                        className="text-mystic-mist hover:text-mystic-amethyst transition-colors"
                        data-testid="contact-email-link"
                      >
                        hello@krystalz.in
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-full bg-mystic-amethyst/20 border border-mystic-amethyst/30 flex items-center justify-center flex-shrink-0">
                      <Phone className="w-5 h-5 text-mystic-amethyst" />
                    </div>
                    <div>
                      <p className="font-medium text-mystic-silver">Call Us</p>
                      <a 
                        href="tel:+919030792052" 
                        className="text-mystic-mist hover:text-mystic-amethyst transition-colors"
                        data-testid="contact-phone-link"
                      >
                        +91 9030792052
                      </a>
                      <p className="text-mystic-mist/60 text-sm mt-1">Mon-Sat, 10am - 7pm IST</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-full bg-mystic-amethyst/20 border border-mystic-amethyst/30 flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-5 h-5 text-mystic-amethyst" />
                    </div>
                    <div>
                      <p className="font-medium text-mystic-silver">Location</p>
                      <p className="text-mystic-mist">Hyderabad, Telangana, India</p>
                      <p className="text-mystic-mist/60 text-sm mt-1">Shipping cosmic energy to all pincodes</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* WhatsApp CTA */}
              <div className="bg-gradient-to-br from-mystic-purple/50 to-mystic-deep rounded-2xl p-8 text-center border border-white/10">
                <MessageCircle className="w-12 h-12 text-whatsapp mx-auto mb-4" />
                <h3 className="font-serif text-2xl text-mystic-silver mb-3 italic">
                  Prefer WhatsApp?
                </h3>
                <p className="text-mystic-mist/80 mb-6">
                  Chat with us directly for quick responses and personalized crystal guidance.
                </p>
                <a
                  href="https://wa.me/919030792052?text=🔮%20Hi!%20I%20have%20a%20question%20about%20your%20crystals."
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="whatsapp-chat-btn"
                >
                  <Button className="bg-whatsapp hover:bg-whatsapp/90 text-white rounded-full px-8 py-6 text-base font-medium whatsapp-pulse">
                    <MessageCircle className="w-5 h-5 mr-2" />
                    Chat on WhatsApp
                  </Button>
                </a>
              </div>

              {/* FAQ Teaser */}
              <div className="glass rounded-2xl p-8 border border-white/10">
                <h3 className="font-serif text-xl text-mystic-silver mb-4 italic">
                  ✧ Frequently Asked ✧
                </h3>
                <div className="space-y-3">
                  <div className="border-b border-white/10 pb-3">
                    <p className="font-medium text-mystic-silver text-sm">Do you ship internationally?</p>
                    <p className="text-mystic-mist/70 text-sm">Currently, we only ship within India.</p>
                  </div>
                  <div className="border-b border-white/10 pb-3">
                    <p className="font-medium text-mystic-silver text-sm">Are your crystals authentic?</p>
                    <p className="text-mystic-mist/70 text-sm">Yes! All crystals are 100% genuine and energetically cleansed.</p>
                  </div>
                  <div>
                    <p className="font-medium text-mystic-silver text-sm">What payment methods do you accept?</p>
                    <p className="text-mystic-mist/70 text-sm">We accept UPI, bank transfer, and COD via WhatsApp orders.</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
